import { PublicShell } from "@/components/layout/PublicShell";
import { useSessionStorage } from "@/auth/storage.tsx";
import { createBackendClient } from "@/client/backend/client";
import { mutate } from "swr";
import { WHOAMI_KEY } from "@/lib/scopes";
import {
	type FC,
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Limrun } from "@limrun/api/client";
import {
	capturePendingAuthCompletion,
	identifyAnalyticsUser,
	isLimrunStaffEmail,
	resetAnalytics,
	resetIdentifiedAnalytics,
} from "@/analytics";

// Helper function to transform URLs when on alternate domain
export const transformUrl = (url: string | undefined): string | undefined => {
	const alternateRootDomain = import.meta.env.VITE_ALTERNATE_ROOT_DOMAIN as string | undefined;
	if (!url || !alternateRootDomain) return url;

	const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
	if (currentHost.includes('limrun.com') || currentHost.includes('limrun.net')) return url;

	// Only replace in the host part, not the path
	const parsedUrl = new URL(url);
	parsedUrl.hostname = parsedUrl.hostname
		.replace(/limrun\.net/g, alternateRootDomain)
		.replace(/limrun\.com/g, alternateRootDomain);
	return parsedUrl.toString();
};

export type AuthContextProps =
	| {
			userTid: string;
			userName: string;
			authnProvider?: string;
			client: ReturnType<typeof createBackendClient>;
			regionClient: Limrun;
			apiEndpoint: string;
			// Null when the session lives only in the httpOnly cookie (every
			// login since the cookie migration). Features that must embed the
			// raw token (e.g. registry commands) are unavailable then.
			token: string | null;
			organizationTid: string;
	  }
	| "loading"
	| "unauthenticated";

export const AuthContext = createContext<AuthContextProps>("loading");

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [sessionToken] = useSessionStorage("token");
	const [organizationTid] = useSessionStorage("organizationTid");
	// User identity from /v1/whoami depends only on the session credentials, so
	// an org switch must not wait on (or be overwritten by) this round trip.
	const [identity, setIdentity] = useState<
		| {
				userTid: string;
				userName: string;
				authnProvider?: string;
				defaultOrganizationTid: string;
				isInternalOrTestUser: boolean;
				email: string;
		  }
		| "loading"
		| "unauthenticated"
	>("loading");

	// Both clients are derived synchronously from session storage so that an org
	// switch takes effect in the same render. Production uses the httpOnly
	// session cookie; localhost uses a tab-scoped token because its cross-site
	// API cookie is unavailable.
	const client = useMemo(
		() =>
			createBackendClient({
				baseUrl: import.meta.env.VITE_API_ENDPOINT as string,
				...(sessionToken
					? { headers: { Authorization: `Bearer ${sessionToken}` } }
					: {}),
			}),
		[sessionToken],
	);
	const regionClient = useMemo(
		() =>
			new Limrun({
				apiKey: sessionToken,
				baseURL: transformUrl(import.meta.env.VITE_API_ENDPOINT as string),
				// Localhost uses header auth because its cross-site API cookie
				// is unavailable. Production uses the session cookie.
				...(sessionToken ? {} : { fetchOptions: { credentials: "include" } }),
				defaultHeaders: {
					// The SDK refuses to run without an apiKey unless the
					// Authorization header is explicitly nulled, which is
					// exactly the cookie-session case.
					...(sessionToken ? {} : { Authorization: null }),
					"X-Limrun-Client": "console",
					...(organizationTid
						? { "X-Limrun-Organization": organizationTid }
						: {}),
				},
			}),
		[sessionToken, organizationTid],
	);

	useEffect(() => {
		// Ignore resolutions from a superseded token so a slow older request
		// cannot overwrite the result of a newer one.
		let stale = false;
		const verifyToken = async () => {
			const user = await client.GET("/v1/whoami");
			if (stale) {
				return;
			}
			if (user.response.status !== 200 || !user.data) {
				setIdentity("unauthenticated");
				return;
			}
			if (user.data.type !== "user" || !user.data.user) {
				setIdentity("unauthenticated");
				return;
			}
			// /v1/whoami is the TypeID API: its `id` fields contain TIDs.
			// The legacy /whoami response instead exposes UUID `id` fields and
			// a separate organization `tid`, so do not interchange the shapes.
			setIdentity({
				userTid: user.data.user.id,
				userName: user.data.user.name,
				authnProvider: user.data.authnProvider,
				defaultOrganizationTid: user.data.user.defaultOrganization.id,
				isInternalOrTestUser: isLimrunStaffEmail(user.data.user.emailAddress),
				email: user.data.user.emailAddress,
			});
			// Pages read seats and switches from the same record through SWR;
			// seeding it here saves a second round trip on every load.
			void mutate(WHOAMI_KEY, user, { revalidate: false });
		};
		verifyToken().catch((error) => {
			if (stale) {
				return;
			}
			console.log(`Token verification failed: ${error}`);
			setIdentity("unauthenticated");
		});
		return () => {
			stale = true;
		};
	}, [client]);

	const authState = useMemo<AuthContextProps>(() => {
		if (identity === "loading" || identity === "unauthenticated") {
			return identity;
		}
		return {
			apiEndpoint: import.meta.env.VITE_API_ENDPOINT as string,
			userTid: identity.userTid,
			userName: identity.userName,
			authnProvider: identity.authnProvider,
			organizationTid: organizationTid ?? identity.defaultOrganizationTid,
			client,
			regionClient,
			token: sessionToken,
		};
	}, [identity, organizationTid, client, regionClient, sessionToken]);

	// Reset analytics only after a real sign-out (explicit logout resets in
	// auth.ts; this covers token expiry). Resetting anonymous visitors would
	// discard the landing page's shared cookie identity before login and
	// break acquisition-to-user stitching.
	const wasAuthenticated = useRef(false);
	const isInternalOrTestUser = typeof identity === "object" && identity.isInternalOrTestUser;
	useLayoutEffect(() => {
		if (authState === "unauthenticated") {
			if (wasAuthenticated.current) {
				wasAuthenticated.current = false;
				resetAnalytics();
			} else {
				resetIdentifiedAnalytics();
			}
			return;
		}
		if (authState !== "loading") {
			wasAuthenticated.current = true;
			identifyAnalyticsUser(authState.userTid, authState.organizationTid, isInternalOrTestUser,
				typeof identity === "object" ? { email: identity.email, username: identity.userName } : undefined);
			capturePendingAuthCompletion();
		}
	}, [authState, isInternalOrTestUser, identity]);

	return (
		<AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
	);
};

export const ProtectedRoute: FC = () => {
	const context = useContext(AuthContext);
	switch (context) {
		case "unauthenticated":
			sessionStorage.setItem("returnTo", window.location.pathname + window.location.search);
			return <Navigate to="/authn/login" />;
		case "loading":
			return <PublicShell><p role="status" className="text-center text-muted-foreground">Loading your account...</p></PublicShell>;
		default:
			sessionStorage.removeItem("returnTo");
			return <Outlet />;
	}
};
